import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ContentFormatBadge from './content-format-badge';
import { ArrowLeft, Loader2, MousePointerClick, Search, Users } from 'lucide-react';
import { AdAccount } from '../../api/ads/ad-account.api';
import { useOrderFunding } from '../../hooks/use-order-funding';
import { approxCaLabel, fundingShortfall, holdNote } from '../../utils/individual-order';
import { showFundingShortfall, showOrderErrorToast } from './order-error-toast';
import { fetchAllCategory } from '../../api/user-profile/user-profile.api';
import { ICategory } from '../../models';
import {
  BrandApplicationForBusiness,
  BrandCampaignDetail as BrandCampaignDetailData,
  BrandCreatorCard,
  BrandDealStatus,
  BrandDealSummary,
  BrandSponsorCandidate,
  BrandTask,
  cancelBrandDeal,
  getBrandCampaignClicks,
  getBrandCampaignDetail,
  getBrandCreatorDirectory,
  getBrandTasksForBusiness,
  inviteBrandCreator,
  submitBusinessRating,
} from '../../api/payment/brand-deal.api';
import { dealStatusLabel, priceRangeLabel, validateDealPrice } from '../../utils/brand-deal';
import { useBrandDealLimits } from '../../hooks/use-brand-deal-limits';
import { countdownLabel, revisionCountLabel, taskStatusLabel } from '../../utils/brand-creative';
import { disputeStatusLabel } from '../../utils/brand-dispute';
import { ratingBadge } from '../../utils/brand-rating';
import BrandCampaignListingPanel from './brand-campaign-listing-panel';
import BrandCampaignApplications, { BrandCampaignApplicationsHandle } from './brand-campaign-applications';
import BrandTaskReview from './brand-task-review';
import BrandCampaignProposals from './brand-campaign-proposals';
import { BrandRatingBox } from './brand-rating-form';
import { creatorCollabPath } from '../../utils/brand-profile';
import BrandBookingDialog from '../brand-live/brand-booking-dialog';
import { BrandLiveRateCard, getBrandLiveRateCard } from '../../api/payment/brand-live.api';
import BrandCampaignPerformance from './brand-campaign-performance';
import BrandConversionSetupPanel from './brand-conversion-setup';
import { useOrdererSanction } from '../brand-sanction/orderer-sanction-context';
import { SANCTION_LOCK_HINT } from '../../utils/brand-sanction';
import { BrandThreadToggle } from './brand-thread-panel';
import BrandExistingPostPicker, { BrandContentSource, ContentSourceToggle } from './brand-existing-post-picker';
import BrandDealContentEditor, { DealContentInfo } from './brand-deal-content-editor';
import { EXISTING_POST_REQUIRED_MESSAGE } from '../../utils/brand-deal-content';
import { BrandBriefInviteList } from './brand-brief-invite-editor';
import { BRIEF_INVITE_FORM_NOTE, briefInviteConfirmText } from '../../utils/brand-brief-invite';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const fmtVnd = (n: number) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const STATUS_GROUP_ORDER = [
  'Invited',
  'Publishing',
  'Live',
  'NeedsReview',
  'Completed',
  'Declined',
  'Expired',
  'Cancelled',
  'Forfeited',
  'PublishFailed',
];

// Khai ngoài component cha — chỉ hiện số liệu tổng, không giữ state riêng, không cần dựng lại.
const Metric: React.FC<{ label: string; value: string; caption?: string }> = ({ label, value, caption }) => (
  <div className="rounded-lg border ui-divider p-3">
    <div className="text-xs ui-muted">{label}</div>
    <div className="text-lg font-bold ui-text">{value}</div>
    {caption ? <div className="text-xs ui-muted">{caption}</div> : null}
  </div>
);

// Khai ngoài component cha (landmine ghi nhớ) — 1 dòng nhiệm vụ trong tab "Nhiệm vụ" (brief sáng tạo,
// M3 Task 7, phía DOANH NGHIỆP): creator, trạng thái, đồng hồ đếm 48 giờ/hạn nộp, số lần đã yêu cầu sửa.
const BusinessTaskRow: React.FC<{ task: BrandTask; nowMs: number; onOpen: () => void }> = ({ task, nowMs, onOpen }) => {
  const deadline =
    task.status === 'Submitted'
      ? countdownLabel(task.reviewDueAt, nowMs)
      : task.status === 'Working' || task.status === 'RevisionRequested'
      ? countdownLabel(task.submitDeadline, nowMs)
      : '';

  return (
    <button type="button" onClick={onOpen} className="flex w-full flex-col gap-1 p-3 text-left text-[14px]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="ui-text font-semibold">{task.creator?.displayName || 'Creator'}</span>
        <span className="ui-muted text-[13px]">{taskStatusLabel(task.status)}</span>
      </div>
      <div className="ui-muted flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px]">
        {deadline && <span>{deadline}</span>}
        <span>Đã yêu cầu sửa {revisionCountLabel(task)} lần</span>
        {task.cancelRequest && <span className="font-semibold text-amber-600 dark:text-amber-400">Có yêu cầu huỷ chờ duyệt</span>}
        {/* Fix round 1 (review Important #1): đánh dấu ngay trên danh sách — trước đây chỉ thấy khi mở
            từng nhiệm vụ, doanh nghiệp nhiều creator không biết nhiệm vụ nào đang bị kháng cáo. */}
        {task.disputeStatus === 'Open' && (
          <span className="font-semibold text-amber-600 dark:text-amber-400">Đang bị kháng cáo</span>
        )}
        {task.disputeStatus && task.disputeStatus !== 'Open' && (
          <span className="font-semibold ui-text">{disputeStatusLabel(task.disputeStatus)}</span>
        )}
      </div>
    </button>
  );
};

interface BrandCampaignDetailProps {
  campaignId: string;
  bizAccounts: AdAccount[];
  onBack: () => void;
  /** M5 (Task 3): chuyển trang cha sang mục "Chờ chấm điểm" — hiện nút "Chấm điểm ngay" trên toast lỗi
   * "Bạn cần chấm điểm…" khi mời/duyệt đơn/đăng sàn bị chặn vì còn deal chưa chấm. */
  onGoToRatingsTab?: () => void;
  /** M5 (fix round 1): gọi sau khi GỬI CHẤM ĐIỂM thành công từ MÀN NÀY (deal M1 trong "Deal theo trạng
   * thái" hoặc nhiệm vụ M3 trong tab "Nhiệm vụ") — cha (`BrandCampaignSection`, dải nhắc/đếm "Chờ chấm
   * điểm") giảm đếm ngay, không phải đợi mở lại mục đó mới đồng bộ lại (review Important #1). */
  onRated?: () => void;
  /** Mở sẵn tab nào khi vào chi tiết — vd bấm 1 đơn ứng tuyển ở mục "Yêu cầu nhận được" thì mở thẳng
   * "Đơn xin tham gia". Brief sáng tạo luôn mở "Nhiệm vụ", bỏ qua giá trị này. */
  initialTab?: 'invite' | 'applications';
}

const BrandCampaignDetailView: React.FC<BrandCampaignDetailProps> = ({
  campaignId,
  bizAccounts,
  onBack,
  onGoToRatingsTab,
  onRated,
  initialTab = 'invite',
}) => {
  const [detail, setDetail] = useState<BrandCampaignDetailData | null>(null);
  const [clicks, setClicks] = useState<{ date: string; clicks: number }[]>([]);
  const [loading, setLoading] = useState(true);

  const [categories, setCategories] = useState<ICategory[]>([]);
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [page, setPage] = useState(1);
  const [directory, setDirectory] = useState<{ items: BrandCreatorCard[]; totalPages: number }>({
    items: [],
    totalPages: 0,
  });
  const [directoryLoading, setDirectoryLoading] = useState(false);

  const [invitingUserId, setInvitingUserId] = useState<string | null>(null);
  // Đang bị khoá tạo việc (RB7/RB8): mời, thương lượng và đặt quảng cáo bị chặn ở BE nên mờ nút.
  const { blocked } = useOrdererSanction();
  const [priceInput, setPriceInput] = useState(500000);
  const [inviteBusy, setInviteBusy] = useState(false);
  // Nguồn nội dung của lời mời (nội dung chiến dịch hoặc bài có sẵn của creator) và hộp sửa nội dung từng lời mời.
  const [inviteSource, setInviteSource] = useState<BrandContentSource>('new');
  const [invitePost, setInvitePost] = useState<BrandSponsorCandidate | null>(null);
  const [editingDeal, setEditingDeal] = useState<BrandDealSummary | null>(null);
  // Lời mời làm brief đang mở hộp "Sửa brief" (chiến dịch brief).
  const [editingBriefId, setEditingBriefId] = useState<string | null>(null);
  const [cancelBusyId, setCancelBusyId] = useState<string | null>(null);
  const [ratingBusyId, setRatingBusyId] = useState<string | null>(null);
  const [bookTarget, setBookTarget] = useState<{ card: BrandCreatorCard; rate: BrandLiveRateCard } | null>(null);
  const openBooking = async (card: BrandCreatorCard) => {
    try {
      const rate = await getBrandLiveRateCard(card.userId);
      if (!rate) {
        toast.error('Creator chưa nhận quảng cáo livestream.');
        return;
      }
      setBookTarget({ card, rate });
    } catch (e) {
      const r = (e as { response?: { status?: number; data?: { message?: string } } })?.response;
      toast.error(
        r?.status === 404
          ? 'Creator chưa nhận quảng cáo livestream.'
          : r?.data?.message || 'Không tải được bảng giá, thử lại sau.'
      );
    }
  };

  // Task 4: tab "Mời trực tiếp" (danh bạ + deal theo trạng thái, đã có từ Task 7) / "Đơn xin tham gia"
  // (sàn mở, M2) + modal "Thương lượng" (mở form mời M1 điền sẵn creator của đơn đang xem). M3 Task 7:
  // brief Creative thay cả 2 tab trên bằng "Nhiệm vụ" (ẩn mời trực tiếp M1 + đơn xin tham gia M2 — brief
  // sáng tạo không dùng 2 đường nhận deal đó, global-constraints).
  const [tab, setTab] = useState<'invite' | 'applications' | 'tasks' | 'proposals'>(initialTab);
  const applicationsRef = useRef<BrandCampaignApplicationsHandle>(null);
  const [negotiateApp, setNegotiateApp] = useState<BrandApplicationForBusiness | null>(null);
  const [negotiatePrice, setNegotiatePrice] = useState(500000);
  const limits = useBrandDealLimits();
  const [negotiateBusy, setNegotiateBusy] = useState(false);

  // M3 Task 7: danh sách nhiệm vụ + màn duyệt bài (bấm 1 dòng ⇒ mở BrandTaskReview).
  const [tasks, setTasks] = useState<BrandTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [selectedTaskDealId, setSelectedTaskDealId] = useState<string | null>(null);
  const [tasksNowMs, setTasksNowMs] = useState(() => Date.now());

  const loadTasks = () => {
    setTasksLoading(true);
    getBrandTasksForBusiness(campaignId)
      .then(setTasks)
      .catch(() => toast.error('Không tải được danh sách nhiệm vụ.'))
      .finally(() => setTasksLoading(false));
  };

  // silent: làm mới ngầm (không hiện vòng xoay, không tháo các khối con đang mở như danh sách đề xuất).
  const load = (silent = false) => {
    if (!silent) setLoading(true);
    Promise.all([getBrandCampaignDetail(campaignId), getBrandCampaignClicks(campaignId, 30)])
      .then(([d, c]) => {
        setDetail(d);
        setClicks(c);
      })
      .catch(() => toast.error('Không tải được chi tiết chiến dịch.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    fetchAllCategory().then(setCategories).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  // Brief ⇒ tab "Nhiệm vụ"; đơn nhận đề xuất ⇒ tab "Đề xuất" (vẫn tải nhiệm vụ cho tab "Nhiệm vụ").
  useEffect(() => {
    const kind = detail?.campaign.kind;
    if (kind !== 'Creative' && kind !== 'Proposal') return;
    setTab(kind === 'Proposal' ? 'proposals' : 'tasks');
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.campaign.kind, campaignId]);

  useEffect(() => {
    const t = setInterval(() => setTasksNowMs(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  // Nguồn tiền đúng tài khoản sở hữu chiến dịch (theo AdAccountId, không theo tên): ví VND hoặc Ca nạp.
  const orderAccount = bizAccounts.find((a) => a.id === detail?.campaign.adAccountId) ?? null;
  const { funding, reload: reloadFunding } = useOrderFunding(orderAccount, detail?.campaign.vndPerCa);
  const isIndividual = funding.kind === 'Individual';

  // Danh bạ creator: tìm/lọc chủ đề/phân trang — debounce ô tìm kiếm.
  useEffect(() => {
    if (detail?.campaign.status !== 'Approved') return;
    setDirectoryLoading(true);
    const t = setTimeout(() => {
      getBrandCreatorDirectory({ q: q.trim() || undefined, categoryId: categoryId || undefined, page, adAccountId: detail?.campaign.adAccountId })
        .then((res) => setDirectory({ items: res.items, totalPages: res.totalPages }))
        .catch(() => toast.error('Không tải được danh bạ creator.'))
        .finally(() => setDirectoryLoading(false));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, categoryId, page, detail?.campaign.status]);

  const openInvite = (userId: string) => {
    setInvitingUserId(userId);
    // Chiến dịch brief đã đăng sàn: điền sẵn giá suất để giá mời khớp giá creator thấy trên sàn (R4).
    setPriceInput(detail?.campaign.kind === 'Creative' && detail.campaign.slotPriceVnd ? detail.campaign.slotPriceVnd : 500000);
    setInviteSource('new');
    setInvitePost(null);
  };

  const confirmInvite = async (card: BrandCreatorCard) => {
    const error = validateDealPrice(priceInput, limits.minPriceVnd, limits.maxPriceVnd);
    if (error) {
      toast.error(error);
      return;
    }
    if (inviteSource === 'existing' && !invitePost) {
      toast.error(EXISTING_POST_REQUIRED_MESSAGE);
      return;
    }
    const parts = [{ unitVnd: priceInput, count: 1 }];
    const shortfall = fundingShortfall(funding, parts, 'Số dư ví không đủ để ký quỹ deal này.');
    if (shortfall) {
      showFundingShortfall(funding, shortfall);
      return;
    }
    const isBriefInvite = detail?.campaign.kind === 'Creative';
    const confirmText = isBriefInvite
      ? briefInviteConfirmText(holdNote(funding, parts), card.displayName)
      : `${holdNote(funding, parts)} để mời ${card.displayName}. Tiếp tục?`;
    if (!window.confirm(confirmText)) return;
    try {
      setInviteBusy(true);
      await inviteBrandCreator(campaignId, card.userId, priceInput, inviteSource === 'existing' ? invitePost?.postId : null);
      toast.success(isBriefInvite ? `Đã mời ${card.displayName} làm brief.` : `Đã mời ${card.displayName}.`);
      setInvitingUserId(null);
      load();
      reloadFunding();
    } catch (e) {
      showOrderErrorToast(e, 'Mời creator thất bại.', onGoToRatingsTab);
    } finally {
      setInviteBusy(false);
    }
  };

  const cancelDeal = async (deal: BrandDealSummary) => {
    const refundText = isIndividual
      ? 'Huỷ lời mời này? Ca đã giữ sẽ được hoàn về Ca nạp.'
      : 'Huỷ lời mời này? Tiền ký quỹ sẽ được hoàn về ví doanh nghiệp.';
    const noReinviteText = detail?.campaign.kind === 'Creative' ? ' Creator này sẽ không mời lại được trong chiến dịch.' : '';
    if (!window.confirm(refundText + noReinviteText)) return;
    try {
      setCancelBusyId(deal.id);
      await cancelBrandDeal(deal.id);
      toast.success('Đã huỷ lời mời và hoàn tiền ký quỹ.');
      load();
      reloadFunding();
    } catch (e) {
      toast.error(errorMessage(e, 'Huỷ lời mời thất bại.'));
    } finally {
      setCancelBusyId(null);
    }
  };

  const rateDeal = async (deal: BrandDealSummary, stars: number, comment: string) => {
    setRatingBusyId(deal.id);
    try {
      await submitBusinessRating(deal.id, stars, comment || undefined);
      toast.success('Đã gửi đánh giá.');
      load();
      onRated?.();
    } catch (e) {
      toast.error(errorMessage(e, 'Không gửi được đánh giá.'));
    } finally {
      setRatingBusyId(null);
    }
  };

  const openNegotiate = (application: BrandApplicationForBusiness) => {
    setNegotiateApp(application);
    setNegotiatePrice(campaign.slotPriceVnd || 500000);
  };

  const confirmNegotiate = async () => {
    if (!negotiateApp) return;
    const error = validateDealPrice(negotiatePrice, limits.minPriceVnd, limits.maxPriceVnd);
    if (error) {
      toast.error(error);
      return;
    }
    const parts = [{ unitVnd: negotiatePrice, count: 1 }];
    const shortfall = fundingShortfall(funding, parts, 'Số dư ví không đủ để ký quỹ deal này.');
    if (shortfall) {
      showFundingShortfall(funding, shortfall);
      return;
    }
    const confirmText = `${holdNote(funding, parts)} để gửi lời mời riêng cho ${negotiateApp.creator.displayName} với giá mới — đơn xin tham gia hiện tại sẽ tự đóng. Tiếp tục?`;
    if (!window.confirm(confirmText)) return;
    try {
      setNegotiateBusy(true);
      // Thương lượng = chỉ gọi lời mời riêng M1 — backend tự đóng đơn Pending với lý do "Đã gửi lời
      // mời riêng" trong cùng lần lưu (global-constraints: KHÔNG gọi reject sau bước này).
      await inviteBrandCreator(campaignId, negotiateApp.creator.userId, negotiatePrice);
      toast.success(`Đã gửi lời mời riêng cho ${negotiateApp.creator.displayName}.`);
      setNegotiateApp(null);
      load();
      reloadFunding();
      applicationsRef.current?.reload();
    } catch (e) {
      showOrderErrorToast(e, 'Gửi lời mời riêng thất bại.', onGoToRatingsTab);
    } finally {
      setNegotiateBusy(false);
    }
  };

  const groupedDeals = useMemo(() => {
    if (!detail) return [] as Array<{ status: string; deals: BrandDealSummary[] }>;
    const byStatus = new Map<string, BrandDealSummary[]>();
    detail.deals.forEach((d) => {
      const list = byStatus.get(d.status) || [];
      list.push(d);
      byStatus.set(d.status, list);
    });
    return STATUS_GROUP_ORDER.filter((s) => byStatus.has(s)).map((s) => ({ status: s, deals: byStatus.get(s) as BrandDealSummary[] }));
  }, [detail]);

  if (loading || !detail) {
    return (
      <div className="flex items-center justify-center p-10">
        <Loader2 className="animate-spin ui-muted" />
      </div>
    );
  }

  const { campaign } = detail;
  const isCreative = campaign.kind === 'Creative';
  const isProposal = campaign.kind === 'Proposal';
  const chartLabels = clicks.map((c) => c.date.slice(5));

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-[#0071A6]">
        <ArrowLeft size={16} /> Quay lại danh sách
      </button>

      <div className="rounded-xl border ui-divider p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-bold ui-text">{campaign.title}</h2>
          <ContentFormatBadge format={campaign.contentFormat} />
          {campaign.visibility && (
            <span className="rounded-full border ui-divider px-2 py-0.5 text-[11px] font-semibold ui-muted">
              {campaign.visibility === 'Public' ? 'Công khai' : 'Riêng tư'}
            </span>
          )}
        </div>
        {isProposal ? (
          <div className="whitespace-pre-wrap text-xs ui-muted">
            Người đặt: {campaign.brandName} · Nhận đề xuất
            {campaign.destinationUrl ? ` · Link đích: ${campaign.destinationUrl}` : ''}
          </div>
        ) : isCreative ? (
          <div className="whitespace-pre-wrap text-xs ui-muted">
            {isIndividual ? 'Người đặt' : 'Doanh nghiệp'}: {campaign.brandName} · Hạn nộp bài {campaign.submitDays} ngày
            {campaign.destinationUrl ? ` · Link đích: ${campaign.destinationUrl}` : ''}
          </div>
        ) : (
          <div className="text-xs ui-muted">
            {isIndividual ? 'Người đặt' : 'Doanh nghiệp'}: {campaign.brandName} · Link đích: {campaign.destinationUrl} · Giữ bài {campaign.holdDays} ngày
          </div>
        )}
        {(isCreative || isProposal) && campaign.briefText && (
          <p className="mt-2 whitespace-pre-wrap text-sm ui-text">{campaign.briefText}</p>
        )}
      </div>

      {/* Task 4: đăng lên sàn / trạng thái sàn / đóng sàn — chỉ hiện khi chiến dịch Approved hoặc đã
          từng đăng sàn (None + chưa Approved thì ẩn hẳn, xử lý trong component). M3 Task 7: brief
          Creative dùng lại NGUYÊN component này, chỉ đổi chữ "Đăng brief"/"Đóng brief" (variant="brief"). */}
      {campaign.visibility === 'Public' && campaign.status === 'Approved' && campaign.listingStatus === 'None' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
          Chiến dịch đã duyệt nhưng chưa tự lên sàn được, ví dụ ví không đủ tiền ký quỹ hoặc tài khoản đang bị hạn chế. Kiểm tra rồi bấm Đăng lên sàn.
        </div>
      )}

      <BrandCampaignListingPanel
        campaignId={campaignId}
        campaign={campaign}
        detail={detail}
        funding={funding}
        onFundsChanged={reloadFunding}
        categories={categories}
        onChanged={(updated) => setDetail(updated)}
        onClosed={() => applicationsRef.current?.reload()}
        variant={isCreative ? 'brief' : 'market'}
        onGoToRatingsTab={onGoToRatingsTab}
      />

      {/* Tổng tiền ký quỹ/đã trả/đã hoàn/phí */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {/* M-6 (final review): "Đã khoá (Hold)" gồm cả Hold của deal sinh từ sàn ("từ sàn", đã tính trong
            "Tiền còn trong ký quỹ sàn" ở BrandCampaignListingPanel) — chú thích để doanh nghiệp không cộng
            dồn nhầm 2 số lại thành ra đếm 2 lần phần suất đã chuyển. */}
        <Metric label="Đã khoá (Hold)" value={fmtVnd(detail.totalHoldVnd)} caption="gồm cả suất từ sàn" />
        <Metric label="Đã trả creator" value={fmtVnd(detail.totalReleaseVnd)} />
        <Metric label="Đã hoàn doanh nghiệp" value={fmtVnd(detail.totalRefundVnd + detail.listingRefundedVnd)} />
        <Metric label="Phí sàn" value={fmtVnd(detail.totalFeeVnd)} />
      </div>

      {/* Đo chuyển đổi (spec 2026-10-05-brand-conversion §3) — hiện cho mọi loại chiến dịch (Direct/Creative),
          không phụ thuộc tab đang mở bên dưới. */}
      <BrandCampaignPerformance campaignId={campaignId} />
      <BrandConversionSetupPanel campaignId={campaignId} />

      {/* Task 4: tab "Mời trực tiếp" (danh bạ + deal, Task 7) / "Đơn xin tham gia" (sàn mở, M2). Brief sáng tạo có thanh tab
          riêng "Nhiệm vụ"/"Mời creator" bên dưới; đơn nhận đề xuất có "Đề xuất"/"Nhiệm vụ". */}
      {!isCreative && !isProposal && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setTab('invite')}
            className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${
              tab === 'invite' ? 'border-[#0057FF] bg-[#0057FF] text-white' : 'ui-divider ui-text'
            }`}
          >
            Mời trực tiếp
          </button>
          <button
            type="button"
            onClick={() => setTab('applications')}
            className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${
              tab === 'applications' ? 'border-[#0057FF] bg-[#0057FF] text-white' : 'ui-divider ui-text'
            }`}
          >
            Đơn xin tham gia{detail.pendingApplications > 0 ? ` (${detail.pendingApplications})` : ''}
          </button>
        </div>
      )}

      {isProposal && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setTab('proposals')}
            className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${
              tab === 'proposals' ? 'border-[#0057FF] bg-[#0057FF] text-white' : 'ui-divider ui-text'
            }`}
          >
            Đề xuất{detail.pendingProposals ? ` (${detail.pendingProposals})` : ''}
          </button>
          <button
            type="button"
            onClick={() => setTab('tasks')}
            className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${
              tab === 'tasks' ? 'border-[#0057FF] bg-[#0057FF] text-white' : 'ui-divider ui-text'
            }`}
          >
            Nhiệm vụ
          </button>
        </div>
      )}

      {isCreative && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setTab('tasks')}
            className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${
              tab === 'tasks' ? 'border-[#0057FF] bg-[#0057FF] text-white' : 'ui-divider ui-text'
            }`}
          >
            Nhiệm vụ
          </button>
          <button
            type="button"
            onClick={() => setTab('invite')}
            className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${
              tab === 'invite' ? 'border-[#0057FF] bg-[#0057FF] text-white' : 'ui-divider ui-text'
            }`}
          >
            Mời creator
          </button>
        </div>
      )}

      {tab === 'proposals' && (
        <div className="rounded-xl border ui-divider p-4">
          <BrandCampaignProposals
            campaignId={campaignId}
            brandName={campaign.brandName}
            reviewDays={campaign.reviewDays}
            onChanged={() => {
              load(true);
              loadTasks();
            }}
          />
        </div>
      )}

      {tab === 'tasks' && (
        <div className="rounded-xl border ui-divider p-4">
          {selectedTaskDealId ? (
            <BrandTaskReview
              dealId={selectedTaskDealId}
              viewerRole="Business"
              onBack={() => setSelectedTaskDealId(null)}
              onChanged={loadTasks}
              onRated={onRated}
            />
          ) : (
            <>
              <div className="mb-3 font-semibold ui-text">Nhiệm vụ</div>
              {tasksLoading ? (
                <p className="text-sm ui-muted">Đang tải…</p>
              ) : tasks.length === 0 ? (
                <p className="text-sm ui-muted">{isProposal ? 'Chưa có đề xuất nào được chọn.' : 'Chưa có creator nào nhận brief này.'}</p>
              ) : (
                <div className="divide-y ui-divider">
                  {tasks.map((t) => (
                    <BusinessTaskRow
                      key={t.dealId}
                      task={t}
                      nowMs={tasksNowMs}
                      onOpen={() => setSelectedTaskDealId(t.dealId)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'applications' && (
        <div className="rounded-xl border ui-divider p-4">
          <BrandCampaignApplications
            ref={applicationsRef}
            campaignId={campaignId}
            onChanged={() => load()}
            onNegotiate={openNegotiate}
            onGoToRatingsTab={onGoToRatingsTab}
          />
        </div>
      )}

      {tab === 'invite' && (
      <>
      {/* Biểu đồ click theo ngày — chiến dịch brief chưa có bài đăng lúc mời nên ẩn */}
      {!isCreative && (
      <div className="rounded-xl border ui-divider p-4">
        <div className="mb-2 text-sm font-semibold ui-text">Click hợp lệ theo ngày (30 ngày gần nhất)</div>
        <div className="h-48">
          <Line
            data={{
              labels: chartLabels,
              datasets: [
                {
                  label: 'Click',
                  data: clicks.map((c) => c.clicks),
                  borderColor: '#0071A6',
                  backgroundColor: 'rgba(0,113,166,0.15)',
                  tension: 0.3,
                  fill: true,
                  pointRadius: 2,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { labels: { boxWidth: 12, font: { size: 11 } } } },
              scales: {
                x: { ticks: { font: { size: 10 } } },
                y: { beginAtZero: true, ticks: { font: { size: 10 } } },
              },
            }}
          />
        </div>
      </div>
      )}

      {/* Danh bạ creator + mời — chỉ mời được khi chiến dịch đã Approved */}
      <div className="rounded-xl border ui-divider p-4 space-y-3">
        <div className="flex items-center gap-2 font-semibold ui-text">
          <Users size={18} className="text-[#0071A6]" /> {isCreative ? 'Mời creator làm brief' : 'Danh bạ creator'}
        </div>
        {campaign.status !== 'Approved' ? (
          <p className="text-sm ui-muted">Chiến dịch cần được admin duyệt mới mời được creator.</p>
        ) : (
          <>
            {blocked && (
              <p className="rounded-lg bg-rose-50 p-2.5 text-xs text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
                {SANCTION_LOCK_HINT}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 ui-muted" />
                <input
                  value={q}
                  onChange={(e) => {
                    setPage(1);
                    setQ(e.target.value);
                  }}
                  placeholder="Tìm theo tên/username"
                  className="w-full rounded-md border ui-divider bg-transparent py-2 pl-8 pr-3 text-sm outline-none ui-text"
                />
              </div>
              <select
                value={categoryId}
                onChange={(e) => {
                  setPage(1);
                  setCategoryId(e.target.value);
                }}
                className="rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
              >
                <option value="">Mọi chủ đề</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {directoryLoading ? (
              <p className="text-sm ui-muted">Đang tải…</p>
            ) : directory.items.length === 0 ? (
              <p className="text-sm ui-muted">Không có creator phù hợp.</p>
            ) : (
              <div className="space-y-2">
                {directory.items.map((card) => (
                  <div key={card.userId} className="rounded-lg border ui-divider p-3">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#EAEFFA] text-sm font-bold text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
                        {card.avatar ? (
                          <img src={card.avatar} alt="" className="size-full object-cover" />
                        ) : (
                          card.displayName.trim()[0]?.toUpperCase() || '?'
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2">
                          <span className="truncate font-medium ui-text">{card.displayName}</span>
                          {creatorCollabPath(card.userName) ? (
                            <a
                              href={creatorCollabPath(card.userName) as string}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 text-xs ui-muted underline-offset-2 hover:underline"
                            >
                              {ratingBadge(card.ratingAverage, card.ratingCount ?? 0)}
                            </a>
                          ) : (
                            <span className="shrink-0 text-xs ui-muted">{ratingBadge(card.ratingAverage, card.ratingCount ?? 0)}</span>
                          )}
                          {card.restrictedLarge && (
                            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                              Hạn chế chiến dịch lớn
                            </span>
                          )}
                        </div>
                        <div className="truncate text-xs ui-muted">
                          @{card.userName} · {card.followers.toLocaleString('vi-VN')} người theo dõi ·{' '}
                          {card.completedDeals} deal đã hoàn thành
                          {card.categories.length ? ` · ${card.categories.join(', ')}` : ''}
                        </div>
                        {(card.postPriceVnd || card.liveMonthlyPriceVnd || card.liveHourlyPriceVnd) && (
                          <div className="truncate text-xs ui-text">
                            {[
                              card.postPriceVnd ? `Đăng bài ${fmtVnd(card.postPriceVnd)}` : '',
                              card.liveMonthlyPriceVnd
                                ? `Livestream tháng ${fmtVnd(card.liveMonthlyPriceVnd)}/${card.liveMonthlyHours} giờ`
                                : '',
                              card.liveHourlyPriceVnd ? `Livestream ${fmtVnd(card.liveHourlyPriceVnd)}/giờ` : '',
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        )}
                      </div>
                      {!isCreative && (card.postPriceVnd || card.hasLiveSlots) && (
                        <button
                          disabled={blocked}
                          title={blocked ? SANCTION_LOCK_HINT : undefined}
                          onClick={() => openBooking(card)}
                          className="shrink-0 rounded-md border border-[#0057FF] px-3 py-1.5 text-sm font-semibold text-[#0057FF] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Đặt quảng cáo
                        </button>
                      )}
                      {invitingUserId !== card.userId && (
                        <button
                          disabled={blocked}
                          title={blocked ? SANCTION_LOCK_HINT : undefined}
                          onClick={() => openInvite(card.userId)}
                          className="shrink-0 rounded-md bg-[#0057FF] px-3 py-1.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Mời
                        </button>
                      )}
                    </div>
                    {invitingUserId === card.userId && (
                      <div className="mt-3 space-y-2 rounded-md bg-slate-50 p-3 dark:bg-slate-800/50">
                        <label className="block text-xs ui-muted">
                          Giá deal ({priceRangeLabel(limits.minPriceVnd, limits.maxPriceVnd)})
                          <input
                            type="number"
                            value={priceInput}
                            onChange={(e) => setPriceInput(Number(e.target.value))}
                            className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
                          />
                        </label>
                        <p className="text-[12px] ui-text">
                          {holdNote(funding, [{ unitVnd: priceInput, count: 1 }])}.
                          {funding.kind === 'Individual' && (
                            <span className="ml-1 font-semibold text-[#0057FF] dark:text-blue-300">
                              {approxCaLabel([{ unitVnd: priceInput, count: 1 }], funding.vndPerCa)}
                            </span>
                          )}
                        </p>
                        {isCreative ? (
                          <p className="text-[12px] ui-muted">{BRIEF_INVITE_FORM_NOTE}</p>
                        ) : (
                          <ContentSourceToggle
                            value={inviteSource}
                            onChange={(v) => {
                              setInviteSource(v);
                              setInvitePost(null);
                            }}
                          />
                        )}
                        {!isCreative && inviteSource === 'existing' && (
                          <BrandExistingPostPicker
                            campaignId={campaignId}
                            creatorId={card.userId}
                            selectedPostId={invitePost?.postId ?? null}
                            onSelect={setInvitePost}
                          />
                        )}
                        <div className="flex gap-2">
                          <button
                            disabled={inviteBusy || blocked}
                            onClick={() => confirmInvite(card)}
                            className="rounded-md bg-[#0057FF] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
                          >
                            {inviteBusy ? 'Đang gửi…' : 'Xác nhận mời'}
                          </button>
                          <button
                            disabled={inviteBusy}
                            onClick={() => setInvitingUserId(null)}
                            className="rounded-md border ui-divider px-3 py-1.5 text-sm ui-text"
                          >
                            Huỷ
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {directory.totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 text-sm ui-text">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-md border ui-divider px-3 py-1 disabled:opacity-40"
                >
                  Trước
                </button>
                <span className="ui-muted">
                  Trang {page}/{directory.totalPages}
                </span>
                <button
                  disabled={page >= directory.totalPages}
                  onClick={() => setPage((p) => Math.min(directory.totalPages, p + 1))}
                  className="rounded-md border ui-divider px-3 py-1 disabled:opacity-40"
                >
                  Sau
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isCreative && (
        <BrandBriefInviteList
          deals={detail.deals}
          editingId={editingBriefId}
          cancelBusyId={cancelBusyId}
          onEdit={(d) => setEditingBriefId(d.id)}
          onCloseEditor={() => setEditingBriefId(null)}
          onSaved={() => {
            setEditingBriefId(null);
            load(true);
          }}
          onCancel={cancelDeal}
        />
      )}

      {/* Bảng deal theo trạng thái */}
      {!isCreative && (
      <div className="rounded-xl border ui-divider p-4 space-y-3">
        <div className="font-semibold ui-text">Deal theo trạng thái</div>
        {editingDeal && (
          <BrandDealContentEditor
            deal={editingDeal}
            campaignId={campaignId}
            contentFormat={detail?.campaign.contentFormat ?? null}
            onClose={() => setEditingDeal(null)}
            onSaved={() => {
              setEditingDeal(null);
              load();
            }}
          />
        )}
        {groupedDeals.length === 0 ? (
          <p className="text-sm ui-muted">Chưa có deal nào.</p>
        ) : (
          groupedDeals.map((group) => (
            <div key={group.status} className="space-y-1.5">
              <div className="text-xs font-semibold uppercase tracking-wide ui-muted">
                {dealStatusLabel(group.status as BrandDealStatus)} ({group.deals.length})
              </div>
              {group.deals.map((d) => (
                <div key={d.id} className="flex flex-col gap-2 rounded-md border ui-divider p-2.5 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 ui-text">
                      <span>Giá {fmtVnd(d.priceVnd)}</span>
                      <span className="ui-muted">Kết thúc: {formatDate(d.endsAt)}</span>
                      <span className="inline-flex items-center gap-1 ui-muted">
                        <MousePointerClick size={13} /> {d.validClicks} click
                      </span>
                      {d.postId && (
                        <a
                          href={`/share/post/${d.postId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-[#0057FF]"
                        >
                          Xem bài
                        </a>
                      )}
                    </div>
                    {d.status === 'Invited' && (
                      <button
                        disabled={cancelBusyId === d.id}
                        onClick={() => cancelDeal(d)}
                        className="shrink-0 rounded-md border border-rose-200 px-3 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-900/40 dark:hover:bg-rose-900/20"
                      >
                        Huỷ lời mời
                      </button>
                    )}
                  </div>
                  {/* M5: ô chấm điểm creator + đánh giá đã công bố — ẩn hẳn nếu deal chưa mở chấm. */}
                  <BrandRatingBox
                    ratingDueAt={d.ratingDueAt}
                    myRating={d.myRating}
                    theirRating={d.theirRating}
                    counterpartLabel="Creator đánh giá bạn"
                    busy={ratingBusyId === d.id}
                    onSubmit={(stars, comment) => rateDeal(d, stars, comment)}
                  />
                  <DealContentInfo deal={d} onEdit={() => setEditingDeal(d)} />
                  <BrandThreadToggle threadId={d.threadId} />
                </div>
              ))}
            </div>
          ))
        )}
      </div>
      )}
      </>
      )}

      {/* Task 4: modal "Thương lượng" — mở form mời M1 điền sẵn creator của đơn đang xem. */}
      {negotiateApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="ui-card w-full max-w-[420px] space-y-3 p-4">
            <div className="font-semibold ui-text">Thương lượng với {negotiateApp.creator.displayName}</div>
            <p className="text-xs ui-muted">
              Gửi lời mời riêng với giá mới — đơn xin tham gia hiện tại của {negotiateApp.creator.displayName} sẽ tự
              đóng.
            </p>
            <label className="block text-sm ui-text">
              Giá deal ({priceRangeLabel(limits.minPriceVnd, limits.maxPriceVnd)})
              <input
                type="number"
                value={negotiatePrice}
                onChange={(e) => setNegotiatePrice(Number(e.target.value))}
                className="mt-1 w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-sm outline-none ui-text"
              />
            </label>
            <p className="text-[12px] ui-text">
              {holdNote(funding, [{ unitVnd: negotiatePrice, count: 1 }])}.
              {funding.kind === 'Individual' && (
                <span className="ml-1 font-semibold text-[#0057FF] dark:text-blue-300">
                  {approxCaLabel([{ unitVnd: negotiatePrice, count: 1 }], funding.vndPerCa)}
                </span>
              )}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={negotiateBusy || blocked}
                title={blocked ? SANCTION_LOCK_HINT : undefined}
                onClick={confirmNegotiate}
                className="rounded-md bg-[#0057FF] px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
              >
                {negotiateBusy ? 'Đang gửi…' : 'Gửi lời mời riêng'}
              </button>
              <button
                type="button"
                disabled={negotiateBusy}
                onClick={() => setNegotiateApp(null)}
                className="rounded-md border ui-divider px-3 py-1.5 text-sm ui-text"
              >
                Huỷ
              </button>
            </div>
          </div>
        </div>
      )}

      {bookTarget && (
        <BrandBookingDialog
          creatorId={bookTarget.card.userId}
          creatorName={bookTarget.card.displayName}
          rateCard={bookTarget.rate}
          bizAccounts={bizAccounts}
          onClose={() => setBookTarget(null)}
          onCreated={() => setBookTarget(null)}
          onGoToRatingsTab={onGoToRatingsTab}
        />
      )}
    </div>
  );
};

export default BrandCampaignDetailView;
